/**
 * Drone Delivery Animation
 *
 * Purely visual effect: a drone flies in on a pre-computed orbital trajectory
 * through the system's gravity wells, lands offset from the player's surface
 * position, deploys a crate, then ascends and departs.
 */

import * as THREE from 'three';
import { rk4Step, checkCollision, getSurfaceRadius } from '../core/physics';
import type { Vec3, PlanetState } from '../types';
import { buildLanderBody, buildDeliveryCrate } from './transitModels';
import { G, PHYS_DT } from '../constants';

// ---------------------------------------------------------------------------
// Tuning Constants
// ---------------------------------------------------------------------------

const DRONE_ENTRY_RADIUS = 60;        // distance from system centroid for entry points
const DRONE_SIM_DT = PHYS_DT * 4;    // coarser timestep (1/30s) for path sim
const DRONE_MAX_SIM_STEPS = 600;      // max sim steps (~20s of flight)
const DRONE_NUM_CANDIDATES = 12;      // entry angles to try
const DRONE_CLOSE_PASS_MULT = 2.5;    // radius multiplier for "close pass" detection
const DRONE_ARRIVAL_ALT = 3.0;        // hover height above surface
const DRONE_APPROACH_DURATION = 3.0;  // approach animation time
const DRONE_PATH_SAMPLES = 200;       // resampled waypoints for smooth playback
const DRONE_LANDING_OFFSET = 0.6;     // radians (~35deg) offset from player on surface
const DRONE_HOMING_FRAC = 0.15;       // last 15% of path blends toward arrival point

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DroneExhaustParticle {
  active: boolean;
  life: number;
  maxLife: number;
  vx: number;
  vy: number;
  vz: number;
  phase: number;
  freq: number;
}

export interface DroneDeliveryEffect {
  rootGroup: THREE.Group;
  model: THREE.Group;
  rocket: THREE.Group;
  flame: THREE.Group;
  crate: THREE.Group;
  scorch: THREE.Mesh;
  target: THREE.Mesh;

  state: 'approach' | 'descend' | 'landed' | 'ascend' | 'done';
  stateTime: number;
  enginePower: number;

  // Orbital approach path
  orbitalPath: THREE.Vector3[];   // pre-computed waypoints
  approachDuration: number;       // seconds

  // Landing info
  planetCenter: THREE.Vector3;
  surfaceNormal: THREE.Vector3;
  surfaceRadius: number;
  surfaceQuat: THREE.Quaternion;

  // Exhaust
  exhData: DroneExhaustParticle[];
  exhPos: Float32Array;
  exhGeo: THREE.BufferGeometry;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

// ---------------------------------------------------------------------------
// Orbital Approach Path Computation
// ---------------------------------------------------------------------------

/**
 * Compute a gravity-influenced approach path from a distant entry point
 * to the arrival point above the target planet. Tries multiple entry angles
 * and picks the one that passes near the most planets (visual interest).
 */
function computeOrbitalApproach(
  arrivalPoint: Vec3,
  targetPlanetIndex: number,
  planets: PlanetState[],
): Vec3[] {
  // System centroid (mass-weighted)
  let totalMass = 0;
  let cx = 0, cy = 0, cz = 0;
  for (const p of planets) {
    if (!p.alive) continue;
    totalMass += p.mass;
    cx += p.position.x * p.mass;
    cy += p.position.y * p.mass;
    cz += p.position.z * p.mass;
  }
  if (totalMass > 0) {
    cx /= totalMass; cy /= totalMass; cz /= totalMass;
  }
  const centroid: Vec3 = { x: cx, y: cy, z: cz };

  const targetPlanet = planets[targetPlanetIndex];

  // Direction from centroid to arrival point (used to build orthonormal basis)
  let toArrival: Vec3 = {
    x: arrivalPoint.x - centroid.x,
    y: arrivalPoint.y - centroid.y,
    z: arrivalPoint.z - centroid.z,
  };
  const toArrLen = Math.sqrt(toArrival.x ** 2 + toArrival.y ** 2 + toArrival.z ** 2);
  if (toArrLen > 0.001) {
    toArrival = { x: toArrival.x / toArrLen, y: toArrival.y / toArrLen, z: toArrival.z / toArrLen };
  } else {
    toArrival = { x: 1, y: 0, z: 0 };
  }

  // Build orthonormal basis for entry point distribution
  let arbitrary: Vec3 = Math.abs(toArrival.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  const basisU: Vec3 = {
    x: toArrival.y * arbitrary.z - toArrival.z * arbitrary.y,
    y: toArrival.z * arbitrary.x - toArrival.x * arbitrary.z,
    z: toArrival.x * arbitrary.y - toArrival.y * arbitrary.x,
  };
  const bULen = Math.sqrt(basisU.x ** 2 + basisU.y ** 2 + basisU.z ** 2);
  if (bULen > 0.001) { basisU.x /= bULen; basisU.y /= bULen; basisU.z /= bULen; }
  const basisV: Vec3 = {
    x: toArrival.y * basisU.z - toArrival.z * basisU.y,
    y: toArrival.z * basisU.x - toArrival.x * basisU.z,
    z: toArrival.x * basisU.y - toArrival.y * basisU.x,
  };

  let bestPath: Vec3[] | null = null;
  let bestScore = -Infinity;

  for (let ci = 0; ci < DRONE_NUM_CANDIDATES; ci++) {
    const angle = (ci / DRONE_NUM_CANDIDATES) * Math.PI * 2;
    const cosA = Math.cos(angle);
    const sinA = Math.sin(angle);

    // Entry point on sphere around centroid
    const entryDir: Vec3 = {
      x: cosA * basisU.x + sinA * basisV.x,
      y: cosA * basisU.y + sinA * basisV.y,
      z: cosA * basisU.z + sinA * basisV.z,
    };
    const entryPos: Vec3 = {
      x: centroid.x + entryDir.x * DRONE_ENTRY_RADIUS,
      y: centroid.y + entryDir.y * DRONE_ENTRY_RADIUS,
      z: centroid.z + entryDir.z * DRONE_ENTRY_RADIUS,
    };

    // Direction from entry toward centroid (inward)
    const inwardDir: Vec3 = {
      x: centroid.x - entryPos.x,
      y: centroid.y - entryPos.y,
      z: centroid.z - entryPos.z,
    };
    const inLen = Math.sqrt(inwardDir.x ** 2 + inwardDir.y ** 2 + inwardDir.z ** 2);
    if (inLen > 0.001) { inwardDir.x /= inLen; inwardDir.y /= inLen; inwardDir.z /= inLen; }

    // Tangential direction (perpendicular to inward, in the plane of entry)
    const tangDir: Vec3 = {
      x: entryDir.y * inwardDir.z - entryDir.z * inwardDir.y,
      y: entryDir.z * inwardDir.x - entryDir.x * inwardDir.z,
      z: entryDir.x * inwardDir.y - entryDir.y * inwardDir.x,
    };
    const tLen = Math.sqrt(tangDir.x ** 2 + tangDir.y ** 2 + tangDir.z ** 2);
    if (tLen > 0.001) { tangDir.x /= tLen; tangDir.y /= tLen; tangDir.z /= tLen; }

    // Vary speed factor across candidates
    const speedFactor = 0.6 + (ci / DRONE_NUM_CANDIDATES) * 0.7;
    const orbitalSpeed = Math.sqrt(G * totalMass / Math.max(DRONE_ENTRY_RADIUS, 1)) * speedFactor;

    // Velocity: mostly tangential with small inward bias toward target
    const toTarget: Vec3 = {
      x: targetPlanet.position.x - entryPos.x,
      y: targetPlanet.position.y - entryPos.y,
      z: targetPlanet.position.z - entryPos.z,
    };
    const ttLen = Math.sqrt(toTarget.x ** 2 + toTarget.y ** 2 + toTarget.z ** 2);
    if (ttLen > 0.001) { toTarget.x /= ttLen; toTarget.y /= ttLen; toTarget.z /= ttLen; }

    const vel: Vec3 = {
      x: tangDir.x * orbitalSpeed * 0.7 + toTarget.x * orbitalSpeed * 0.3,
      y: tangDir.y * orbitalSpeed * 0.7 + toTarget.y * orbitalSpeed * 0.3,
      z: tangDir.z * orbitalSpeed * 0.7 + toTarget.z * orbitalSpeed * 0.3,
    };

    // Simulate
    let pos: Vec3 = { ...entryPos };
    let curVel: Vec3 = { ...vel };
    const path: Vec3[] = [{ ...pos }];
    let arrived = false;
    const passedPlanets = new Set<number>();

    for (let step = 0; step < DRONE_MAX_SIM_STEPS; step++) {
      const result = rk4Step(pos, curVel, DRONE_SIM_DT, planets);
      pos = result.pos;
      curVel = result.vel;
      path.push({ ...pos });

      // Check collision (heightmap-aware) — discard if hits a planet
      if (checkCollision(pos, planets)) break;

      // Track close passes
      for (let pi = 0; pi < planets.length; pi++) {
        if (!planets[pi].alive || pi === targetPlanetIndex) continue;
        const dx = pos.x - planets[pi].position.x;
        const dy = pos.y - planets[pi].position.y;
        const dz = pos.z - planets[pi].position.z;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        if (dist < planets[pi].radius * DRONE_CLOSE_PASS_MULT) {
          passedPlanets.add(pi);
        }
      }

      // Check arrival near target planet
      const ax = pos.x - targetPlanet.position.x;
      const ay = pos.y - targetPlanet.position.y;
      const az = pos.z - targetPlanet.position.z;
      const distFromTarget = Math.sqrt(ax * ax + ay * ay + az * az);
      if (distFromTarget <= targetPlanet.radius + DRONE_ARRIVAL_ALT) {
        arrived = true;
        break;
      }
    }

    const score = passedPlanets.size * 10 + (arrived ? 50 : 0);
    if (score > bestScore) {
      bestScore = score;
      bestPath = path;
    }
  }

  // Fallback: direct curved path if nothing arrived
  if (!bestPath || bestPath.length < 2) {
    const entryPos: Vec3 = {
      x: centroid.x + DRONE_ENTRY_RADIUS,
      y: centroid.y,
      z: centroid.z,
    };
    bestPath = [entryPos, arrivalPoint];
  }

  // Resample to uniform arc-length waypoints
  const resampled = resamplePath(bestPath, DRONE_PATH_SAMPLES);

  // Homing splice: last 15% lerps toward exact arrival point
  const homingStart = Math.floor(DRONE_PATH_SAMPLES * (1 - DRONE_HOMING_FRAC));
  for (let i = homingStart; i < resampled.length; i++) {
    const blend = (i - homingStart) / (resampled.length - homingStart);
    resampled[i] = {
      x: resampled[i].x + (arrivalPoint.x - resampled[i].x) * blend,
      y: resampled[i].y + (arrivalPoint.y - resampled[i].y) * blend,
      z: resampled[i].z + (arrivalPoint.z - resampled[i].z) * blend,
    };
  }

  // Post-path planet deflection: push any waypoints inside planets to the surface
  deflectPathFromPlanets(resampled, planets, targetPlanetIndex);

  return resampled;
}

/**
 * Push any path waypoints that are inside a planet (heightmap-aware) to just
 * above the surface. Skips the target planet so the ship can approach it.
 */
function deflectPathFromPlanets(
  path: Vec3[],
  planets: PlanetState[],
  targetPlanetIndex: number,
): void {
  const MARGIN = 0.5; // clearance above surface
  for (let i = 0; i < path.length; i++) {
    const pt = path[i];
    for (let pi = 0; pi < planets.length; pi++) {
      if (pi === targetPlanetIndex || !planets[pi].alive) continue;
      const p = planets[pi];
      const dx = pt.x - p.position.x;
      const dy = pt.y - p.position.y;
      const dz = pt.z - p.position.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      // Quick reject: if well outside, skip
      if (dist > p.radius * 1.2) continue;
      const surfR = getSurfaceRadius(p, { x: dx, y: dy, z: dz });
      const safeR = surfR + MARGIN;
      if (dist < safeR && dist > 0.001) {
        // Push outward to safe radius
        const scale = safeR / dist;
        path[i] = {
          x: p.position.x + dx * scale,
          y: p.position.y + dy * scale,
          z: p.position.z + dz * scale,
        };
      }
    }
  }
}

/**
 * Resample a path to N uniformly-spaced waypoints by arc length.
 */
function resamplePath(path: Vec3[], n: number): Vec3[] {
  if (path.length < 2) return [{ ...path[0] }];

  // Compute cumulative arc lengths
  const arcLengths: number[] = [0];
  for (let i = 1; i < path.length; i++) {
    const dx = path[i].x - path[i - 1].x;
    const dy = path[i].y - path[i - 1].y;
    const dz = path[i].z - path[i - 1].z;
    arcLengths.push(arcLengths[i - 1] + Math.sqrt(dx * dx + dy * dy + dz * dz));
  }
  const totalLength = arcLengths[arcLengths.length - 1];
  if (totalLength < 0.001) return [{ ...path[0] }];

  const result: Vec3[] = [];
  let segIdx = 0;

  for (let i = 0; i < n; i++) {
    const targetLen = (i / (n - 1)) * totalLength;

    // Advance segment index
    while (segIdx < arcLengths.length - 2 && arcLengths[segIdx + 1] < targetLen) {
      segIdx++;
    }

    const segLen = arcLengths[segIdx + 1] - arcLengths[segIdx];
    const t = segLen > 0.0001 ? (targetLen - arcLengths[segIdx]) / segLen : 0;
    const a = path[segIdx];
    const b = path[segIdx + 1];

    result.push({
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
      z: a.z + (b.z - a.z) * t,
    });
  }

  return result;
}

// ---------------------------------------------------------------------------
// Build Drone Model
// ---------------------------------------------------------------------------

function buildDroneModel(): {
  group: THREE.Group;
  rocket: THREE.Group;
  flame: THREE.Group;
  crate: THREE.Group;
  scorch: THREE.Mesh;
  target: THREE.Mesh;
  exhData: DroneExhaustParticle[];
  exhPos: Float32Array;
  exhGeo: THREE.BufferGeometry;
} {
  const g = new THREE.Group();

  // Landing target on ground
  const target = new THREE.Mesh(
    new THREE.RingGeometry(0.3, 0.35, 16),
    new THREE.MeshBasicMaterial({ color: '#406040', transparent: true, opacity: 0.4, side: THREE.DoubleSide }),
  );
  target.rotation.x = -Math.PI / 2;
  target.position.y = 0.01;
  g.add(target);

  // Scorch mark
  const scorch = new THREE.Mesh(
    new THREE.CylinderGeometry(0.25, 0.25, 0.005, 12),
    new THREE.MeshBasicMaterial({ color: '#1a1816', transparent: true, opacity: 0.0 }),
  );
  scorch.position.y = 0.005;
  g.add(scorch);

  // Lander assembly (transitModels.ts)
  const rocket = buildLanderBody();

  // Rocket starts at local Y offset (will be positioned by state machine)
  rocket.position.y = 0;
  g.add(rocket);

  // Engine flame group
  const flame = new THREE.Group();
  const outerFlame = new THREE.Mesh(
    new THREE.ConeGeometry(0.08, 0.5, 8),
    new THREE.MeshBasicMaterial({ color: '#e8a040', transparent: true, opacity: 0.7 }),
  );
  outerFlame.position.y = 0.25;
  outerFlame.name = 'outerFlame';
  flame.add(outerFlame);
  const innerFlame = new THREE.Mesh(
    new THREE.ConeGeometry(0.03, 0.35, 6),
    new THREE.MeshBasicMaterial({ color: '#f0e0c0', transparent: true, opacity: 0.9 }),
  );
  innerFlame.position.y = 0.18;
  innerFlame.name = 'innerFlame';
  flame.add(innerFlame);
  // Additive glow blob instead of a PointLight (adding a light recompiles every lit shader)
  const engineGlow = new THREE.Mesh(
    new THREE.SphereGeometry(0.16, 10, 8),
    new THREE.MeshBasicMaterial({ color: '#e8a040', transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }),
  );
  engineGlow.name = 'engineGlow';
  engineGlow.position.y = 0.05;
  flame.add(engineGlow);

  const exhCount = 20;
  const exhGeo = new THREE.BufferGeometry();
  const exhPos = new Float32Array(exhCount * 3);
  const exhData: DroneExhaustParticle[] = [];
  for (let i = 0; i < exhCount; i++) {
    exhPos[i * 3] = 0;
    exhPos[i * 3 + 1] = -5;
    exhPos[i * 3 + 2] = 0;
    exhData.push({
      life: 0, maxLife: 0, active: false,
      vx: 0, vy: 0, vz: 0,
      phase: Math.random() * Math.PI * 2,
      freq: 6 + Math.random() * 8,
    });
  }
  exhGeo.setAttribute('position', new THREE.BufferAttribute(exhPos, 3));
  const exhMat = new THREE.PointsMaterial({
    color: '#e8c060', size: 0.06, transparent: true, opacity: 0.6,
    blending: THREE.AdditiveBlending, depthWrite: false,
  });
  const exhPoints = new THREE.Points(exhGeo, exhMat);
  exhPoints.name = 'exhPoints';
  flame.add(exhPoints);

  flame.position.y = 0;
  flame.visible = false;
  g.add(flame);

  // Crate (transitModels.ts)
  const crate = buildDeliveryCrate();
  crate.position.y = -1;
  crate.visible = false;
  g.add(crate);

  return { group: g, rocket, flame, crate, scorch, target, exhData, exhPos, exhGeo };
}

// ---------------------------------------------------------------------------
// Create Drone Delivery
// ---------------------------------------------------------------------------

export function createDroneDelivery(
  scene: THREE.Scene,
  playerWorldPos: THREE.Vector3,
  planetPos: THREE.Vector3,
  surfaceNormal: THREE.Vector3,
  surfaceRadius: number,
  planets: PlanetState[],
): DroneDeliveryEffect {
  const built = buildDroneModel();

  // Scale model relative to planet size
  const modelScale = surfaceRadius * 0.15;
  built.group.scale.setScalar(modelScale);

  const rootGroup = new THREE.Group();
  rootGroup.add(built.group);

  // Compute offset landing direction (Rodrigues' rotation of surfaceNormal)
  const up = new THREE.Vector3(0, 1, 0);
  let tangent = new THREE.Vector3().crossVectors(surfaceNormal, up);
  if (tangent.lengthSq() < 0.001) {
    tangent.crossVectors(surfaceNormal, new THREE.Vector3(1, 0, 0));
  }
  tangent.normalize();

  // Rodrigues' rotation: rotate surfaceNormal by DRONE_LANDING_OFFSET radians toward tangent
  const cosOff = Math.cos(DRONE_LANDING_OFFSET);
  const sinOff = Math.sin(DRONE_LANDING_OFFSET);
  // Rotation axis = cross(surfaceNormal, tangent) — already perpendicular
  const rotAxis = new THREE.Vector3().crossVectors(surfaceNormal, tangent).normalize();
  // Rodrigues: v_rot = v*cos(θ) + (k×v)*sin(θ) + k*(k·v)*(1-cos(θ))
  const kCrossN = new THREE.Vector3().crossVectors(rotAxis, surfaceNormal);
  const kDotN = rotAxis.dot(surfaceNormal);
  const offsetDir = new THREE.Vector3(
    surfaceNormal.x * cosOff + kCrossN.x * sinOff + rotAxis.x * kDotN * (1 - cosOff),
    surfaceNormal.y * cosOff + kCrossN.y * sinOff + rotAxis.y * kDotN * (1 - cosOff),
    surfaceNormal.z * cosOff + kCrossN.z * sinOff + rotAxis.z * kDotN * (1 - cosOff),
  ).normalize();

  // Landing orientation: align local Y-up to offsetDir
  const surfaceQuat = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(0, 1, 0),
    offsetDir,
  );

  // Landing and arrival positions using offset direction
  const landingPos = new THREE.Vector3()
    .copy(planetPos)
    .addScaledVector(offsetDir, surfaceRadius);

  const arrivalPoint: Vec3 = {
    x: planetPos.x + offsetDir.x * (surfaceRadius + DRONE_ARRIVAL_ALT),
    y: planetPos.y + offsetDir.y * (surfaceRadius + DRONE_ARRIVAL_ALT),
    z: planetPos.z + offsetDir.z * (surfaceRadius + DRONE_ARRIVAL_ALT),
  };

  // Find target planet index
  let targetPlanetIndex = 0;
  const px = planetPos.x, py = planetPos.y, pz = planetPos.z;
  for (let i = 0; i < planets.length; i++) {
    const pp = planets[i].position;
    if (Math.abs(pp.x - px) < 0.01 && Math.abs(pp.y - py) < 0.01 && Math.abs(pp.z - pz) < 0.01) {
      targetPlanetIndex = i;
      break;
    }
  }

  // Compute orbital approach path
  const pathVec3 = computeOrbitalApproach(arrivalPoint, targetPlanetIndex, planets);

  // Convert to THREE.Vector3 array
  const orbitalPath = pathVec3.map(p => new THREE.Vector3(p.x, p.y, p.z));

  // Position at start of path
  if (orbitalPath.length > 0) {
    rootGroup.position.copy(orbitalPath[0]);
  }
  scene.add(rootGroup);

  return {
    rootGroup,
    model: built.group,
    rocket: built.rocket,
    flame: built.flame,
    crate: built.crate,
    scorch: built.scorch,
    target: built.target,

    state: 'approach',
    stateTime: 0,
    enginePower: 0.7,

    orbitalPath,
    approachDuration: DRONE_APPROACH_DURATION,

    planetCenter: planetPos.clone(),
    surfaceNormal: offsetDir.clone(),
    surfaceRadius,
    surfaceQuat,

    exhData: built.exhData,
    exhPos: built.exhPos,
    exhGeo: built.exhGeo,
  };
}

// ---------------------------------------------------------------------------
// Update Drone Delivery (returns true = still running, false = done)
// ---------------------------------------------------------------------------

export function updateDroneDelivery(effect: DroneDeliveryEffect, dt: number, time: number): boolean {
  effect.stateTime += dt;
  const st = effect.stateTime;

  switch (effect.state) {
    // -------------------------------------------------------------------
    // APPROACH: fly along orbital path waypoints
    // -------------------------------------------------------------------
    case 'approach': {
      const rawT = Math.min(st / effect.approachDuration, 1);
      const t = smoothstep(rawT);

      const pathLen = effect.orbitalPath.length;
      const pathT = t * (pathLen - 1);
      const idx = Math.min(Math.floor(pathT), pathLen - 2);
      const frac = pathT - idx;

      // Lerp between adjacent waypoints
      const a = effect.orbitalPath[idx];
      const b = effect.orbitalPath[idx + 1];
      const pos = new THREE.Vector3(
        a.x + (b.x - a.x) * frac,
        a.y + (b.y - a.y) * frac,
        a.z + (b.z - a.z) * frac,
      );
      effect.rootGroup.position.copy(pos);

      // Orient along path tangent (next - current waypoint)
      const nextIdx = Math.min(idx + 1, pathLen - 1);
      const tang = new THREE.Vector3().subVectors(effect.orbitalPath[nextIdx], a);
      if (tang.lengthSq() > 0.0001) {
        tang.normalize();
        const lookTarget = new THREE.Vector3().copy(pos).add(tang);
        effect.model.lookAt(lookTarget);
      }

      // In last 20%, slerp toward landing orientation
      if (rawT > 0.8) {
        const blend = (rawT - 0.8) / 0.2;
        effect.model.quaternion.slerp(effect.surfaceQuat, blend);
      }

      effect.enginePower = 0.7;
      effect.flame.visible = true;

      if (rawT >= 1) {
        effect.state = 'descend';
        effect.stateTime = 0;
        // Snap orientation to landing quaternion
        effect.model.quaternion.copy(effect.surfaceQuat);
      }
      break;
    }

    // -------------------------------------------------------------------
    // DESCEND: lower from arrival point to surface
    // -------------------------------------------------------------------
    case 'descend': {
      const duration = 1.5;
      const rawT = Math.min(st / duration, 1);
      // Ease-out: fast at start, slow at end
      const t = 1 - (1 - rawT) * (1 - rawT);

      const startHeight = DRONE_ARRIVAL_ALT;
      const endHeight = 0.22 * effect.surfaceRadius * 0.15; // scaled landing height
      const currentHeight = startHeight + (endHeight - startHeight) * t;

      const pos = new THREE.Vector3()
        .copy(effect.planetCenter)
        .addScaledVector(effect.surfaceNormal, effect.surfaceRadius + currentHeight);

      // Subtle sway
      const swayX = Math.sin(time * 2.3) * 0.015;
      const swayZ = Math.cos(time * 2.8) * 0.01;
      const sideDir = new THREE.Vector3().crossVectors(effect.surfaceNormal, new THREE.Vector3(0, 1, 0));
      if (sideDir.lengthSq() < 0.001) sideDir.crossVectors(effect.surfaceNormal, new THREE.Vector3(1, 0, 0));
      sideDir.normalize();
      const fwdDir = new THREE.Vector3().crossVectors(sideDir, effect.surfaceNormal).normalize();
      pos.addScaledVector(sideDir, swayX);
      pos.addScaledVector(fwdDir, swayZ);

      effect.rootGroup.position.copy(pos);
      effect.model.quaternion.copy(effect.surfaceQuat);

      // Subtle tilt from sway
      const tiltQuat = new THREE.Quaternion().setFromEuler(
        new THREE.Euler(Math.sin(time * 1.8) * 0.02, 0, Math.cos(time * 2.1) * 0.02),
      );
      effect.model.quaternion.multiply(tiltQuat);

      const dist = (currentHeight - endHeight) / startHeight;
      effect.enginePower = dist * 0.8;
      effect.flame.visible = true;

      // Target ring pulse
      (effect.target.material as THREE.MeshBasicMaterial).opacity = 0.3 + Math.sin(time * 6) * 0.15;

      if (rawT >= 1) {
        effect.state = 'landed';
        effect.stateTime = 0;
        effect.enginePower = 0;
        effect.flame.visible = false;
        // Show scorch
        (effect.scorch.material as THREE.MeshBasicMaterial).opacity = 0.4;
      }
      break;
    }

    // -------------------------------------------------------------------
    // LANDED: sit on surface, deploy crate
    // -------------------------------------------------------------------
    case 'landed': {
      effect.enginePower = 0;
      effect.flame.visible = false;

      // Show crate at 0.5s
      if (st > 0.5 && !effect.crate.visible) {
        effect.crate.visible = true;
        effect.crate.position.set(0.25, 0.05, 0.12);
        effect.crate.rotation.y = 0.3;
      }

      // Transition to ascend at 1.5s
      if (st > 1.5) {
        effect.state = 'ascend';
        effect.stateTime = 0;
        effect.flame.visible = true;
        effect.enginePower = 1;
      }
      break;
    }

    // -------------------------------------------------------------------
    // ASCEND: climb along surfaceNormal, accelerating
    // -------------------------------------------------------------------
    case 'ascend': {
      const climbSpeed = (1.0 + st * 2.5) * dt;
      const currentPos = effect.rootGroup.position;
      currentPos.addScaledVector(effect.surfaceNormal, climbSpeed);

      effect.model.quaternion.copy(effect.surfaceQuat);
      effect.enginePower = Math.max(0, 1.0 - st * 0.3);

      const distFromSurface = new THREE.Vector3()
        .copy(currentPos)
        .sub(effect.planetCenter)
        .length() - effect.surfaceRadius;

      if (distFromSurface > 15) {
        effect.state = 'done';
        effect.flame.visible = false;
        effect.enginePower = 0;
      }
      break;
    }

    case 'done':
      return false;
  }

  // Update flame visuals
  updateFlame(effect, dt, time);

  return true;
}

// ---------------------------------------------------------------------------
// Flame Animation
// ---------------------------------------------------------------------------

function updateFlame(effect: DroneDeliveryEffect, dt: number, time: number): void {
  if (!effect.flame.visible) return;

  // Position flame at rocket base
  effect.flame.position.copy(effect.rocket.position);
  effect.flame.position.y -= 0.04;

  const power = effect.enginePower;
  const flicker = 0.9 + Math.sin(time * 15) * 0.08 + Math.sin(time * 23) * 0.04;

  const outer = effect.flame.getObjectByName('outerFlame') as THREE.Mesh | undefined;
  const inner = effect.flame.getObjectByName('innerFlame') as THREE.Mesh | undefined;
  const glow = effect.flame.getObjectByName('engineGlow') as THREE.Mesh | undefined;

  if (outer) {
    outer.scale.set(flicker * power, power * (0.8 + Math.sin(time * 11) * 0.15), flicker * power);
    (outer.material as THREE.MeshBasicMaterial).opacity = 0.6 * power;
  }
  if (inner) {
    inner.scale.set(flicker * power * 0.8, power * 0.9, flicker * power * 0.8);
    (inner.material as THREE.MeshBasicMaterial).opacity = 0.85 * power;
  }
  if (glow) {
    (glow.material as THREE.MeshBasicMaterial).opacity = Math.min(0.55, 0.45 * power);
    glow.scale.setScalar(0.6 + power * 0.7);
  }

  // Exhaust particles
  const exhPts = effect.flame.getObjectByName('exhPoints') as THREE.Points | undefined;
  if (exhPts && power > 0.1) {
    const pos = effect.exhPos;
    for (let i = 0; i < effect.exhData.length; i++) {
      const p = effect.exhData[i];
      if (!p.active) {
        p.active = true;
        p.life = 0;
        p.maxLife = 0.4 + Math.random() * 0.6;
        pos[i * 3] = (Math.random() - 0.5) * 0.08;
        pos[i * 3 + 1] = 0;
        pos[i * 3 + 2] = (Math.random() - 0.5) * 0.08;
        p.vx = (Math.random() - 0.5) * 0.5;
        p.vy = -(1.5 + Math.random() * 1.5) * power;
        p.vz = (Math.random() - 0.5) * 0.5;
      }
      p.life += dt;
      if (p.life > p.maxLife) {
        p.active = false;
        pos[i * 3 + 1] = -5;
      } else {
        const twinkle = Math.sin(time * p.freq + p.phase) * 0.02;
        pos[i * 3] += (p.vx + twinkle) * dt;
        pos[i * 3 + 1] += p.vy * dt;
        pos[i * 3 + 2] += (p.vz + twinkle) * dt;
        p.vy *= 0.96;
      }
    }
    effect.exhGeo.attributes.position.needsUpdate = true;
    (exhPts.material as THREE.PointsMaterial).opacity = 0.5 * power;
  }
}

// ---------------------------------------------------------------------------
// Dispose
// ---------------------------------------------------------------------------

export function disposeDroneDelivery(scene: THREE.Scene, effect: DroneDeliveryEffect): void {
  scene.remove(effect.rootGroup);
  effect.rootGroup.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      child.geometry.dispose();
      if (Array.isArray(child.material)) {
        child.material.forEach(m => m.dispose());
      } else {
        child.material.dispose();
      }
    } else if (child instanceof THREE.Points) {
      child.geometry.dispose();
      if (Array.isArray(child.material)) {
        child.material.forEach(m => m.dispose());
      } else {
        child.material.dispose();
      }
    } else if (child instanceof THREE.Light) {
      child.dispose();
    }
  });
}
